/**
 * Create or edit a product. Left: identity, line, workshop, stock flags and
 * display order; middle: main and custom pictures (one to three, by the size
 * display of the line); right: the stores of the line, read-only, coloured by
 * stock.
 */
import { useMemo, useState, type SubmitEvent } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCircleNotch,
  faExclamationCircle,
} from '@fortawesome/free-solid-svg-icons';
import type { Store } from '@/shared/config/app-config';
import { compareCodeUnits } from '@/shared/lib/compare';
import { Dialog } from '@/shared/ui/Dialog';
import { PictureField } from '@/shared/ui/PictureField';
import { usePicture } from '@/shared/ui/use-picture';
import { CheckboxTree } from '@/shared/ui/checkbox-tree/CheckboxTree';
import { workshopsQueryOptions } from '@/features/stores/api';
import {
  storeLinks,
  storeWarning,
} from '@/features/product-lines/product-line-form';
import { appConfigsQueryOptions } from '@/shared/config/app-configs';
import {
  createProduct,
  deleteProductPicture,
  fetchCentralStocks,
  fetchStoreStocks,
  productQueryOptions,
  updateProduct,
  uploadProductPicture,
  type Product,
  type ProductPictureType,
  type ProductSize,
  type Stock,
} from './api';
import {
  centralStockQuantity,
  customPictureLabels,
  emptyProductForm,
  formToProduct,
  productPictureUrl,
  productStoreIds,
  productToForm,
  sizeDisplayOf,
  stockCodes,
  storeColor,
  validateProductForm,
  type ProductFormErrors,
  type ProductFormValues,
  type ProductLineSummary,
} from './logic';
import { Select } from '@/shared/ui/Select';
import { optionsOf } from '@/shared/ui/select-options';
import { kebabCase } from '@/shared/lib/kebab-case';

export interface ProductDialogProps {
  item: Product | null;
  stores: readonly Store[];
  lines: readonly ProductLineSummary[];
  onClose: (result: 'Confirm' | 'Cancel') => void;
}

/** Form values before any edit: the fetched product, or an empty form (with the workshop of the row while loading). */
const initialValues = (
  item: Product | null,
  fetched: Product | undefined,
): ProductFormValues => {
  if (!item) return emptyProductForm();
  if (fetched) return productToForm(fetched);
  return { ...emptyProductForm(), workshop: item.workshopId ?? '' };
};

/** "M3 quantity" of an existing product: 0 without sizes once loaded, the central stock once fetched, unknown meanwhile. */
const loadedQuantity = (
  sizes: readonly ProductSize[],
  productLoaded: boolean,
  stocks: readonly Stock[] | null,
): number | null => {
  if (sizes.length === 0) return productLoaded ? 0 : null;
  return stocks ? centralStockQuantity(sizes, stocks) : null;
};

export function ProductDialog({
  item,
  stores,
  lines,
  onClose,
}: Readonly<ProductDialogProps>) {
  const creating = !item;
  const product = useQuery({
    ...productQueryOptions(item?.id ?? ''),
    enabled: !!item,
  });
  const { data: workshops = [] } = useQuery(workshopsQueryOptions());
  const { data: appConfigs } = useQuery({
    ...appConfigsQueryOptions(),
    enabled: !!item,
  });
  const stocksActive = !!appConfigs?.find(
    (entry) => entry.type === 'stocksActive',
  )?.value;
  const sizes = useMemo(() => product.data?.sizes ?? [], [product.data]);

  // Store stocks (colours of the tree), only when the stock feature is on.
  const storeStocks = useQuery({
    queryKey: ['stocks', 'store', item?.id, sizes.length] as const,
    queryFn: () => {
      const codes = stockCodes(sizes, 'stock');
      return fetchStoreStocks(codes.ean, codes.sku);
    },
    enabled: !!item && stocksActive && product.isSuccess,
    retry: false,
  });
  // Central stock: the "M3 quantity" shown next to the checkbox.
  const centralStocks = useQuery({
    queryKey: ['stocks', 'central', item?.id, sizes.length] as const,
    queryFn: () => {
      const codes = stockCodes(sizes, 'centralFlow');
      return fetchCentralStocks(codes.ean, codes.sku);
    },
    enabled: !!item && product.isSuccess && sizes.length > 0,
    retry: false,
  });

  // Edits start from the fetched record.
  const [edits, setEdits] = useState<ProductFormValues | null>(null);
  const values = edits ?? initialValues(item, product.data);
  const set = <K extends keyof ProductFormValues>(
    key: K,
    value: ProductFormValues[K],
  ) => {
    setEdits({ ...values, [key]: value });
    setDirty((prev) => new Set(prev).add(key));
  };
  const [dirty, setDirty] = useState<Set<keyof ProductFormValues>>(
    () => new Set(),
  );
  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors] = useState<ProductFormErrors>({});
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const liveErrors = validateProductForm(values);
  const invalidClass = (key: keyof ProductFormValues) =>
    dirty.has(key) && liveErrors[key as keyof ProductFormErrors]
      ? 'invalid'
      : '';
  const fieldError = (key: keyof ProductFormErrors) => submitted && errors[key];

  const pictures: Record<ProductPictureType, ReturnType<typeof usePicture>> = {
    main: usePicture(),
    custom: usePicture(),
    custom2: usePicture(),
    custom3: usePicture(),
  };

  const sortedWorkshops = [...workshops].sort((a, b) =>
    (a.name ?? '').localeCompare(b.name ?? ''),
  );
  const lineOptions = [...lines]
    .map((line) => line.productLine ?? '')
    .sort(compareCodeUnits);
  const sizeDisplay = sizeDisplayOf(lines, values.line);
  const rows = storeLinks(stores);
  // The tree is read-only and shows the stores of the line the product belonged to when opened.
  const selectedStores = productStoreIds(lines, item?.line);
  const productHasWorkshop = !!item?.workshopId;
  const fetchedCentralStocks = centralStocks.isSuccess
    ? centralStocks.data
    : null;
  const quantity = creating
    ? '-'
    : loadedQuantity(sizes, product.isSuccess, fetchedCentralStocks);
  const colorContext = {
    stores,
    sizes,
    stocks: storeStocks.data ?? null,
    stocksActive,
    stockControl: values.stockControl,
  };

  /** Creates or updates the record; resolves to its id (undefined when the API sent none). */
  const saveRecord = async (): Promise<string | undefined> => {
    const data = formToProduct(values, selectedStores, Date.now());
    try {
      if (!item) return (await createProduct(data))?.id;
      await updateProduct({ ...data, id: item.id });
      return item.id;
    } catch (error) {
      throw new Error(
        creating ? 'Failed to create product' : 'Failed to update product',
        { cause: error },
      );
    }
  };

  /** Uploads or removes every picture the user touched. */
  const savePictures = async (id: string): Promise<void> => {
    try {
      for (const type of Object.keys(pictures) as ProductPictureType[]) {
        const picture = pictures[type];
        if (!picture.touched) continue;
        if (picture.file) await uploadProductPicture(id, picture.file, type);
        else await deleteProductPicture(id, type);
      }
    } catch (error) {
      throw new Error(
        creating
          ? 'Failed to upload/remove product pictures'
          : 'Failed to update product pictures',
        { cause: error },
      );
    }
  };

  const save = useMutation({
    mutationFn: async () => {
      const id = await saveRecord();
      if (id) await savePictures(id);
    },
    onSuccess: () => onClose('Confirm'),
    onError: (error: Error) => setErrorMessage(error.message),
  });

  const onSubmit = (event: SubmitEvent) => {
    event.preventDefault();
    setSubmitted(true);
    setErrorMessage(null);
    const nextErrors = validateProductForm(values);
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length) {
      setErrorMessage('Please fix the errors');
      return;
    }
    save.mutate();
  };

  const checkbox = (
    key: 'centralStockOnly' | 'stockControl' | 'centralStockControl',
    domId: string,
    label: React.ReactNode,
  ) => (
    <div className="mb-2">
      <div className="styled-input-square styled-input-single">
        <input
          type="checkbox"
          id={domId}
          name={key}
          data-testid={`product-form-${kebabCase(key)}`}
          checked={values[key]}
          onChange={(event) => set(key, event.target.checked)}
        />
        <label htmlFor={domId}>{label}</label>
      </div>
    </div>
  );

  const warning = (message: string | null) =>
    message ? (
      <span title={message} className="ml-2 text-[orange]">
        <FontAwesomeIcon icon={faExclamationCircle} />
        <span className="sr-only">{message}</span>
      </span>
    ) : null;

  return (
    <Dialog
      title={creating ? 'Product creation' : 'Product edit'}
      onClose={() => onClose('Cancel')}
      width="950px"
      testId="product-dialog"
    >
      <form className="form popin-form" onSubmit={onSubmit} noValidate>
        <div className="bs-row">
          <div className="col-span-4">
            <div className="mb-4">
              <label className="field-label" htmlFor="product-reference">
                Commercial reference
              </label>
              <input
                id="product-reference"
                data-testid="product-form-reference"
                className={`field ${invalidClass('reference')}`}
                name="reference"
                placeholder="Commercial reference"
                value={values.reference}
                onChange={(event) => set('reference', event.target.value)}
              />
              <small
                hidden={!fieldError('reference')}
                data-testid="product-form-reference-error"
              >
                {errors.reference}
              </small>
            </div>
            <div className="mb-4">
              <label className="field-label" htmlFor="product-name">
                Product Name
              </label>
              <input
                id="product-name"
                data-testid="product-form-name"
                className={`field ${invalidClass('name')}`}
                name="name"
                placeholder="Name"
                value={values.name}
                onChange={(event) => set('name', event.target.value)}
              />
              <small
                hidden={!fieldError('name')}
                data-testid="product-form-name-error"
              >
                {errors.name}
              </small>
            </div>
            <div className="mb-4">
              <label className="field-label" htmlFor="product-line">
                Product Line
              </label>
              <div className={`field field-selector ${invalidClass('line')}`}>
                <Select
                  id="product-line"
                  name="line"
                  placeholder="--Select line--"
                  options={optionsOf(lineOptions)}
                  value={values.line}
                  onChange={(line) => set('line', line)}
                  testId="product-form-line"
                />
              </div>
              <small
                hidden={!fieldError('line')}
                data-testid="product-form-line-error"
              >
                {errors.line}
              </small>
            </div>
            <div className="mb-4">
              <label className="field-label" htmlFor="product-workshop">
                Central Workshop
              </label>
              <div className="field field-selector">
                <Select
                  id="product-workshop"
                  name="workshop"
                  placeholder="--Select workshop--"
                  options={sortedWorkshops.map((workshop) => ({
                    value: workshop.id,
                    label: workshop.name ?? '',
                  }))}
                  value={values.workshop}
                  onChange={(workshop) => set('workshop', workshop)}
                  testId="product-form-workshop"
                />
              </div>
            </div>
            {checkbox(
              'centralStockOnly',
              'centralStockOnly',
              'Central stock only',
            )}
            {checkbox(
              'stockControl',
              'is-stock-control-enabled',
              'Boutique Stock Control Active',
            )}
            {checkbox(
              'centralStockControl',
              'is-central-stock-control-enabled',
              <>
                Central Stock Control Active (M3 quantity ={' '}
                {quantity == null ? (
                  <FontAwesomeIcon
                    icon={faCircleNotch}
                    spin
                    aria-label="Loading"
                  />
                ) : (
                  <span data-testid="product-form-central-stock-quantity">
                    {quantity}
                  </span>
                )}
                )
              </>,
            )}
            <div className="mb-4">
              <label className="field-label" htmlFor="product-displayOrder">
                Display order
              </label>
              <input
                id="product-displayOrder"
                data-testid="product-form-display-order"
                className={`field ${invalidClass('displayOrder')}`}
                type="number"
                min={1}
                step={1}
                name="displayOrder"
                placeholder="Display order"
                value={values.displayOrder}
                onChange={(event) => set('displayOrder', event.target.value)}
              />
              <small
                hidden={!fieldError('displayOrder')}
                data-testid="product-form-display-order-error"
              >
                {errors.displayOrder}
              </small>
            </div>
            {errorMessage && (
              <div
                className="alert mb-4"
                role="alert"
                data-testid="product-form-error"
              >
                <span className="error-msg">{errorMessage}</span>
              </div>
            )}
            <div className="mt-2 flex justify-center">
              <button
                className="button button-form"
                type="submit"
                disabled={save.isPending}
                data-testid="product-form-save-button"
              >
                Save
              </button>
            </div>
          </div>

          <div className="col-span-3">
            <PictureField
              id="product-main-picture"
              name="mainPicture"
              label="Main picture"
              picture={pictures.main}
              serverUrl={productPictureUrl(item?.id, 'main')}
              testId="product-form-main-picture"
            />
            {customPictureLabels(sizeDisplay).map(({ type, label }) => (
              <PictureField
                key={type}
                id={`product-${type}-picture`}
                name={`${type}Picture`}
                label={label}
                picture={pictures[type]}
                serverUrl={productPictureUrl(item?.id, type)}
                testId={`product-form-${type}-picture`}
              />
            ))}
          </div>

          <div className="col-span-5 store-list">
            <span className="field-label">Available in store</span>
            <div
              className="checkbox-group"
              role="region"
              aria-label="Available in store"
            >
              <CheckboxTree
                rows={rows}
                breakdown={['region', 'country']}
                idKey="id"
                labelKey="label"
                selectedIds={selectedStores}
                onChange={() => undefined}
                readOnly
                testId="product-form-stores"
                nodeColor={(node) =>
                  storeColor(colorContext, {
                    level: node.level,
                    label: node.label,
                    storeId: node.row?.id,
                  })
                }
                renderLeafRight={(row) =>
                  warning(
                    storeWarning(stores, productHasWorkshop, {
                      level: 2,
                      storeId: row.id,
                    }),
                  )
                }
                renderBranchRight={(branch, level) =>
                  warning(
                    storeWarning(stores, productHasWorkshop, {
                      level,
                      country: branch.label,
                    }),
                  )
                }
              />
            </div>
          </div>
        </div>
      </form>
    </Dialog>
  );
}
